import { useConfig, useValidation, type Rules } from '@seek/libs-shared';
import { Heading, Stack } from 'braid-design-system';
import { useEffect } from 'react';
import { DEFAULT_VARIANT_SCHEMA_VERSION } from '../../../shared/constants/launchDarklyFeatureFlags';
import {
  CURRENT_PAGE_MANAGEMENT,
  trackContinuePressed,
  trackDisplayPage,
  trackSkipQuestionPressed,
} from '../../../shared/tracking/writeAReview/trackingEvents';
import { ReviewPageLayoutNoStepper } from '../../components/ReviewPageLayout/ReviewPageLayoutNoStepper';
import type { Rating } from '../../components/StarSelector/StarSelector';
import { ExecutiveManagementRatingQuestion } from '../../questions/RatingQuesions/ExecutiveManagementRatingQuestion';
import { ManagementDetailsQuestion } from '../../questions/SimpleQuestions/ManagementDetailsQuestion';
import {
  ExperimentTwoProgress,
  type PageProps,
  type ReviewPayload,
} from '../../type';
import { validationRules } from '../../validation/validationRules';

type Props = PageProps & {
  isSubmitting: boolean;
};

export const ManagementPage = ({
  review,
  reviewFormContext,
  setPage,
  setter,
  pageNumber,
  isSubmitting,
  onSubmit,
}: Props) => {
  const config = useConfig();

  useEffect(() => {
    if (reviewFormContext?.variant) {
      trackDisplayPage({
        config,
        context: reviewFormContext,
        currentPage: CURRENT_PAGE_MANAGEMENT,
        variant: reviewFormContext.variant,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewFormContext?.variant]);

  const fields = {
    executiveManagementDescription: review?.executiveManagementDescription,
  };

  const { executiveManagementDescription } = validationRules(config.language);

  const rules = { executiveManagementDescription } as Rules<typeof fields>;

  const {
    getTone,
    getMessage,
    handleValueChangeWithValidation,
    validateWithErrors,
  } = useValidation<ReviewPayload>(fields, rules);

  const handleOnContinue = () => {
    const { isValid } = validateWithErrors();

    if (onSubmit) {
      onSubmit();
    }

    trackContinuePressed({
      config,
      context: reviewFormContext,
      validation: isValid ? 'success' : 'mandatory_questions_not_answered',
      currentPage: CURRENT_PAGE_MANAGEMENT,
      variant: reviewFormContext?.variant ?? DEFAULT_VARIANT_SCHEMA_VERSION,
    });

    if (!isValid) {
      return;
    }
  };

  const handleOnSkip = () => {
    trackSkipQuestionPressed({
      config,
      context: reviewFormContext,
      currentPage: CURRENT_PAGE_MANAGEMENT,
      variant: reviewFormContext?.variant ?? DEFAULT_VARIANT_SCHEMA_VERSION,
    });

    setter({
      ...review,
      executiveManagementRating: undefined,
      executiveManagementDescription: undefined,
    });

    setPage(ExperimentTwoProgress.DiversityAndEqualOpportunity);
  };
  const handleOnChange =
    (updateProperty: keyof ReviewPayload) => (value: Rating) => {
      setter({
        ...review,
        [updateProperty]: value,
      });
    };

  return (
    <ReviewPageLayoutNoStepper
      progress={pageNumber}
      onContinue={handleOnContinue}
      onSkip={handleOnSkip}
      isContinueLoading={isSubmitting}
    >
      <Stack space="large">
        <Heading level="1">Management</Heading>
        <Stack space="small">
          <ExecutiveManagementRatingQuestion
            onChange={handleOnChange('executiveManagementRating')}
            value={review?.executiveManagementRating}
            title="Rate management"
            isRequired={true}
          />
          <ManagementDetailsQuestion
            onChange={handleValueChangeWithValidation({
              setter,
              validatorProperty: 'executiveManagementDescription',
              previousValue: review,
            })}
            value={review?.executiveManagementDescription}
            tone={getTone('executiveManagementDescription')}
            message={getMessage('executiveManagementDescription')}
          />
        </Stack>
      </Stack>
    </ReviewPageLayoutNoStepper>
  );
};
